import React from 'react';
import { Helmet } from 'react-helmet-async';
import { BRAND, DEFAULT_OG_IMAGE, absoluteUrl } from '../../lib/siteConfig';

/**
 * Per-route SEO head: title, meta description, canonical, Open Graph,
 * Twitter cards, robots, and any JSON-LD schema objects.
 *
 * Uses react-helmet-async's declarative props API (title/meta/link/script)
 * instead of JSX children — this reliably replaces the document <title>
 * (no duplicates with index.html) and avoids the fork's strict children parser.
 */
export const Seo = ({
  title,
  description = '',
  path = '/',
  image = DEFAULT_OG_IMAGE,
  keywords = [],
  schema,
  noindex = false,
}) => {
  const canonical = absoluteUrl(path);
  const schemas = Array.isArray(schema) ? schema : schema ? [schema] : [];
  const fullTitle = title || BRAND;

  const meta = [
    { name: 'description', content: description },
    { name: 'robots', content: noindex ? 'noindex,nofollow' : 'index,follow,max-image-preview:large' },
    { name: 'author', content: BRAND },
    { name: 'geo.region', content: 'US' },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: BRAND },
    { property: 'og:title', content: fullTitle },
    { property: 'og:description', content: description },
    { property: 'og:url', content: canonical },
    { property: 'og:image', content: image },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: fullTitle },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: image },
  ];
  if (keywords.length > 0) meta.push({ name: 'keywords', content: keywords.join(', ') });

  const link = [{ rel: 'canonical', href: canonical }];
  const script = schemas.map((s) => ({ type: 'application/ld+json', innerHTML: JSON.stringify(s) }));

  return <Helmet title={fullTitle} meta={meta} link={link} script={script} />;
};

export default Seo;
